import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store';
import { Box, TableRow } from '@mui/material';
import dayjs from 'dayjs';

import {
  PageHeader, PageTitle, ListContainer,
  StyledTableContainer, StyledTable, StyledTableBody, StyledTableCell,
  StatusTag, PaginationFooter
} from './styles';
import CustomTableHeader from '../../components/CustomHeader';
import CustomPagination from '../../components/CustomPagination';
import Loader from '../../components/common/Loader';

import { fetchShiftRequests } from '../../modules/shift/actions';
import {
  selectShiftRequests,
  selectShiftRequestsLoading,
  selectShiftRequestsCount,
  selectUpdateShiftRequestStatusSuccess,
  selectCancelShiftRequestSuccess,
  selectCompleteShiftRequestSuccess,
} from '../../modules/shift/selectors';
import { selectOrgProfile } from '../../modules/orgProfile/selectors';
import { IShiftRequest } from '../../modules/shift/types';
import { useBreadcrumb } from '../../context/BreadcrumbContext';
import { useSidebar } from '../../context/SidebarContext';
import DetailModal from '../../components/DetailModal';
import FacilityDetails from './components/FacilityDetails';
import ShiftDetails from './components/ShiftDetails';
import ShiftRequestActions from './components/ShiftRequestActions';


const ShiftRequestsPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const shiftRequests = useSelector(selectShiftRequests);
  const loading = useSelector(selectShiftRequestsLoading);
  const totalCount = useSelector(selectShiftRequestsCount);
  const orgProfile = useSelector(selectOrgProfile);

  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<IShiftRequest | null>(null);


  // Setup Breadcrumbs
  useEffect(() => {
    setActivePage('shift-requests');
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Shift Requests', path: '/shift-requests' },
    ]);
    setIsShow(false);
  }, [setActivePage, setPath, setIsShow]);

  // Fetch Data
  useEffect(() => {
    if (orgProfile?.org_id) {
      dispatch(fetchShiftRequests({
        orgId: orgProfile.org_id,
        page,
        pageSize
      }));
    }
  }, [dispatch, orgProfile, page, pageSize]);

  const updateSuccess = useSelector(selectUpdateShiftRequestStatusSuccess);
  const cancelSuccess = useSelector(selectCancelShiftRequestSuccess);
  const completeSuccess = useSelector(selectCompleteShiftRequestSuccess);

  useEffect(() => {
    if (orgProfile?.org_id && (updateSuccess || cancelSuccess || completeSuccess)) {
      dispatch(fetchShiftRequests({
        orgId: orgProfile.org_id,
        page,
        pageSize
      }));
      if (updateSuccess) {
        setSelectedShift((prev) => prev ? { ...prev, status: 'Progressing' } : null);
      }
      if (cancelSuccess) {
        setSelectedShift((prev) => prev ? { ...prev, status: 'Cancelled' } : null);
      }
      if (completeSuccess) {
        setSelectedShift((prev) => prev ? { ...prev, status: 'Completed' } : null);
      }
    }
  }, [updateSuccess, cancelSuccess, completeSuccess, dispatch, orgProfile, page, pageSize]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalPages = Math.ceil((totalCount || 0) / pageSize);

  const columns = [
    { id: 'shift_id', label: 'Shift ID' },
    { id: 'title', label: 'Title' },
    { id: 'facility', label: 'Facility' },
    { id: 'start_date_time', label: 'Start Time' },
    { id: 'end_date_time', label: 'End Time' },
    { id: 'workers_required', label: 'Workers Req.' },
    { id: 'status', label: 'Status' }
  ];

  return (
    <Box>
      <Loader open={loading} />
      <ListContainer>
        <PageHeader>
          <PageTitle>Shift Requests</PageTitle>
        </PageHeader>

        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={columns}
              order={'asc'}
              orderBy={'shift_id'}
              onRequestSort={() => { }}
            />
            <StyledTableBody>
              {shiftRequests?.length === 0 && !loading && (
                <TableRow>
                  <StyledTableCell colSpan={7} align="center">
                    No shift requests found.
                  </StyledTableCell>
                </TableRow>
              )}
              {shiftRequests?.map((request: IShiftRequest) => (
                <TableRow
                  key={request.shift_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedShift(request);
                    setIsDetailModalOpen(true);
                  }}
                >
                  <StyledTableCell>{request.shift_id}</StyledTableCell>
                  <StyledTableCell>{request.title}</StyledTableCell>
                  <StyledTableCell>{request.facility.facility_name}</StyledTableCell>
                  <StyledTableCell>{dayjs(request.start_date_time).format('MMM D, YYYY h:mm A')}</StyledTableCell>
                  <StyledTableCell>{dayjs(request.end_date_time).format('MMM D, YYYY h:mm A')}</StyledTableCell>
                  <StyledTableCell>{request.workers_required}</StyledTableCell>
                  <StyledTableCell>
                    <StatusTag status={request.status}>
                      {request.status}
                    </StatusTag>
                  </StyledTableCell>
                </TableRow>
              ))}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>

        <PaginationFooter>
          <Box>
            Showing {shiftRequests?.length || 0} of {totalCount || 0} results
          </Box>
          <CustomPagination
            totalPages={totalPages}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </PaginationFooter>
      </ListContainer>

      {
        selectedShift && (
          <DetailModal
            open={isDetailModalOpen}
            onClose={() => setIsDetailModalOpen(false)}
            shiftId={selectedShift.shift_id}
            shiftStatus={selectedShift.status}
          >
            <Box>
              <ShiftDetails
                title={selectedShift.title}
                shiftManager={`${selectedShift.attendance_manager.first_name} ${selectedShift.attendance_manager.last_name}`}
                date={dayjs(selectedShift.start_date_time).format('DD/MM/YYYY')}
                startTime={dayjs(selectedShift.start_date_time).format('DD MMM YYYY HH.mm')}
                endTime={dayjs(selectedShift.end_date_time).format('DD MMM YYYY HH.mm')}
                numberOfWorkers={`${selectedShift.workers_required}`}
                experience={`${selectedShift.experience_required} years`}
                position={selectedShift.position_role}
                additionalNotes={selectedShift.additional_notes || undefined}
                shift_id={selectedShift.shift_id}
              />
              <FacilityDetails
                facilityName={selectedShift.facility.facility_name}
                address={selectedShift.facility.address}
                latitude={selectedShift.facility.latitude}
                longitude={selectedShift.facility.longitude}
              />
              <ShiftRequestActions
                status={selectedShift.status}
                shiftRequestId={selectedShift.shift_id}
              />
            </Box>
          </DetailModal>
        )
      }
    </Box >
  );
};

export default ShiftRequestsPage;
